import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Gift, Star } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { STORYDOODLE_PLUS_MESSAGING } from "../config/messaging";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { useUserAccount } from "../hooks/useUserAccount";
import { apiClient } from "../lib/api";
import { formatTimeUntil } from "../lib/utils";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { ConfirmationModal } from "./ui/confirmation-modal";

interface TrialStatusCardProps {
    className?: string;
}

export function TrialStatusCard({ className = "" }: TrialStatusCardProps) {
    const { user } = useAuth();
    const { toast } = useToast();
    const posthog = usePostHog();
    const { tier, paymentPlan } = useUserAccount();
    const queryClient = useQueryClient();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [isUpgrading, setIsUpgrading] = useState(false);

    // Fetch trial status
    const { data: trialStatus, isLoading } = useQuery({
        queryKey: ["trial-status"],
        queryFn: () => apiClient.getTrialStatus(),
        enabled: !!user,
    });

    // Start trial mutation
    const startTrialMutation = useMutation({
        mutationFn: () => apiClient.startFreeTrial(),
        onSuccess: () => {
            // Invalidate related queries to refresh UI
            queryClient.invalidateQueries({ queryKey: ["user-tier-status"] });
            queryClient.invalidateQueries({ queryKey: ["user-account"] });
            queryClient.invalidateQueries({ queryKey: ["trial-status"] });
            queryClient.invalidateQueries({ queryKey: ["quota-status"] });

            setShowConfirmation(false);
            toast({
                title: "Free trial started! 🎉",
                description: "You now have access to StoryDoodle+ features for 7 days.",
                variant: "success",
            });
        },
        onError: (error: Error) => {
            setShowConfirmation(false);
            toast({
                title: "Failed to start trial",
                description: error.message || "Please try again later.",
                variant: "destructive",
            });
        },
    });

    const handleStartTrial = () => {
        setShowConfirmation(true);
    };

    const handleConfirmTrial = () => {
        startTrialMutation.mutate();
    };

    const handleCloseConfirmation = () => {
        setShowConfirmation(false);
    };

    const handleUpgrade = async (billingCycle: "monthly" | "annual") => {
        setIsUpgrading(true);

        // Track upgrade attempt
        posthog?.capture("trial_upgrade_clicked", {
            billing_cycle: billingCycle,
            timestamp: new Date().toISOString(),
        });

        try {
            const { checkout_url } = await apiClient.createTierCheckout("plus", billingCycle);
            window.location.href = checkout_url;
        } catch (error) {
            console.error("Failed to create checkout:", error);
            toast({
                title: "Failed to start upgrade",
                description: "Please try again later.",
                variant: "destructive",
            });
            setIsUpgrading(false);
        }
    };

    if (!user) return null;

    // Don't show for users who already have paid plans
    if (tier !== "free" && paymentPlan !== "trial") return null;

    // Show loading state
    if (isLoading) {
        return (
            <Card className={className}>
                <CardContent className="p-4">
                    <div className="animate-pulse">
                        <div className="h-4 bg-muted rounded w-1/3 mb-2"></div>
                        <div className="h-3 bg-muted rounded w-2/3"></div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // User is currently on trial
    if (trialStatus?.isCurrentlyOnTrial && trialStatus.trialExpiresAt) {
        const expiresAt = new Date(trialStatus.trialExpiresAt);
        const isExpired = expiresAt <= new Date();

        if (isExpired) {
            return (
                <Card className={`border-destructive/20 bg-destructive/5 ${className}`}>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-destructive">
                            <Star className="h-5 w-5" />
                            StoryDoodle+ Trial Expired
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">{STORYDOODLE_PLUS_MESSAGING.trial.expired}</p>
                    </CardContent>
                </Card>
            );
        }

        return (
            <Card className={`border-primary/20 bg-primary/5 ${className}`}>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-secondary">
                        <Star className="h-5 w-5" />
                        StoryDoodle+ Trial Active
                    </CardTitle>
                    <CardDescription className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>Expires {formatTimeUntil(trialStatus.trialExpiresAt)}</span>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">{STORYDOODLE_PLUS_MESSAGING.trial.active}</p>

                    <div className="space-y-3">
                        <p className="text-sm font-medium text-secondary">
                            Subscribe now to keep access when your trial ends!
                        </p>

                        <div className="flex gap-2">
                            <Button
                                onClick={() => handleUpgrade("monthly")}
                                disabled={isUpgrading}
                                size="sm"
                                variant="outline"
                                className="flex-1"
                            >
                                Monthly - $7/month
                            </Button>
                            <Button
                                onClick={() => handleUpgrade("annual")}
                                disabled={isUpgrading}
                                size="sm"
                                variant="outline"
                                className="flex-1"
                            >
                                Annual - $70/year
                            </Button>
                        </div>

                        <p className="text-xs text-muted-foreground">
                            By subscribing, you agree to our{" "}
                            <a
                                href="/terms"
                                className="text-primary hover:underline"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                billing terms and automatic renewal policy
                            </a>
                            .
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // User has already used their trial
    if (trialStatus?.hasUsedTrial) {
        return null; // Don't show anything for users who already used their trial
    }

    // User is eligible for trial
    return (
        <Card className={`border-success/20 bg-success/5 ${className}`}>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-secondary">
                    <Gift className="h-5 w-5" />
                    {STORYDOODLE_PLUS_MESSAGING.trial.duration}
                </CardTitle>
                <CardDescription>{STORYDOODLE_PLUS_MESSAGING.trial.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    <h4 className="text-sm font-medium">What you'll get:</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                        {STORYDOODLE_PLUS_MESSAGING.trial.benefits.map((benefit, index) => (
                            <li key={index}>• {benefit}</li>
                        ))}
                    </ul>
                </div>

                <Button onClick={handleStartTrial} disabled={startTrialMutation.isPending} className="w-full sm:w-auto">
                    Start Free Trial
                </Button>

                <p className="text-xs text-muted-foreground">No credit card required • Cancels automatically</p>
            </CardContent>

            <ConfirmationModal
                isOpen={showConfirmation}
                onClose={handleCloseConfirmation}
                onConfirm={handleConfirmTrial}
                title="Start Your Free Trial?"
                description={STORYDOODLE_PLUS_MESSAGING.trial.confirmDescription}
                confirmButtonText="Yes, Start My 7-Day Trial"
                isLoading={startTrialMutation.isPending}
                loadingText="Starting Trial..."
            />
        </Card>
    );
}
